import type { components } from 'generated/api'
import type { FeatureKey, RoleKey } from 'generated/features'
import { browserLanguage } from 'utils/translate'

// Actions
export const RESET_PASSWORD_CONFIRM = 'auth/RESET_PASSWORD_CONFIRM'
export const resetPasswordConfirm = ({ password, token, uid }) => ({
  type: RESET_PASSWORD_CONFIRM,
  payload: { password, token, uid },
})

export const RESET_PASSWORD_CONFIRM_FAILED =
  'auth/RESET_PASSWORD_CONFIRM_FAILED'
export const resetPasswordConfirmFailed = error => ({
  type: RESET_PASSWORD_CONFIRM_FAILED,
  error,
})

export const RESET_PASSWORD = 'auth/RESET_PASSWORD'
export const RESET_PASSWORD_FAILED = 'auth/RESET_PASSWORD_FAILED'
export const resetPassword = email => ({
  type: RESET_PASSWORD,
  payload: { email },
})
export const resetPasswordFailed = error => ({
  type: RESET_PASSWORD_FAILED,
  error,
})

export const LOG_IN = 'auth/LOG_IN'
export const LOG_IN_SUCCESS = 'auth/LOG_IN_SUCCESS'
export const LOG_IN_FAILED = 'auth/LOG_IN_FAILED'
export const LOG_OUT = 'auth/LOG_OUT'
export const logIn = (email, password) => ({
  type: LOG_IN,
  payload: { email, password },
})
export const loginSuccess = ({ key }) => ({
  type: LOG_IN_SUCCESS,
  payload: { key },
})
export const loginFailed = error => ({ type: LOG_IN_FAILED, error })
export const logOut = () => ({ type: LOG_OUT, payload: {} })

export const SWITCH_ACCOUNT = 'auth/SWITCH_ACCOUNT'
export const switchAccount = account => ({
  type: SWITCH_ACCOUNT,
  payload: account,
})

export const REGISTER_ACCOUNT = 'auth/REGISTER_ACCOUNT'
export const REGISTER_ACCOUNT_SUCCESS = 'auth/REGISTER_ACCOUNT_SUCCESS'
export const REGISTER_ACCOUNT_FAILED = 'auth/REGISTER_ACCOUNT_FAILED'
export const registerAccount = data => ({
  type: REGISTER_ACCOUNT,
  payload: data,
})
export const registerAccountSuccess = (_response?: unknown) => ({
  type: REGISTER_ACCOUNT_SUCCESS,
})
export const registerAccountFailed = error => ({
  type: REGISTER_ACCOUNT_FAILED,
  error,
})

export const REQUEST_USER = 'auth/REQUEST_USER'
export const REQUEST_USER_SUCCESS = 'auth/REQUEST_USER_SUCCESS'
export const REQUEST_USER_FAILED = 'auth/REQUEST_USER_FAILED'
export const requestUser = () => ({ type: REQUEST_USER, payload: {} })
export const requestUserSuccess = data => ({
  type: REQUEST_USER_SUCCESS,
  payload: data,
})
export const requestUserFailed = error => ({ type: REQUEST_USER_FAILED, error })

export const SET_LANGUAGE = 'auth/SET_LANGUAGE'
export const setLanguage = lc => ({ type: SET_LANGUAGE, payload: lc })

// User athentication authState enumerator
export const AUTHSTATUS = {
  UNKNOWN: 'unknown',
  PENDING: 'pending',
  LOGGED_IN: 'logged in',
  LOGGED_OUT: 'logged out',
  ERROR: 'error',
}

// Selectors
export const getUser = state => state?.auth || {}
export const getAccounts = state => state?.auth?.accounts
const getActiveAccount = state => state?.auth?.active_account

export const getCurrentAccount = state => {
  const accountId = getActiveAccount(state)
  return getAccounts(state).find(account => account?.id === accountId)
}
export const getLanguage = state => state?.auth?.language

// django rest framework authentication token
export const getAuthToken = state => state?.auth?.key

// feature flags and roles of the current account, empty until the user is loaded.
// The empty default is shared so useSelector sees the same reference each time
const noFlags = Object.freeze({})

export const getFeatures = (state): Partial<Record<FeatureKey, boolean>> =>
  getCurrentAccount(state)?.features ?? noFlags

export const getRoles = (state): Partial<Record<RoleKey, boolean>> =>
  getCurrentAccount(state)?.roles ?? noFlags

// django orm permissions for current user
// getPermission('app.change_job') reads auth.permissions.job.change.app
export const getPermission = permission => {
  const path = permission.split(/[^a-z]/g).reverse()
  return state =>
    Boolean(path.reduce((node, key) => node?.[key], state?.auth?.permissions))
}

// reducers
// an account of the user, as the user endpoint sends it
export type UserAccount = components['schemas']['Me']['accounts'][number]
type Account = Partial<UserAccount> & { current?: boolean }

const initialState = {
  language: browserLanguage(),
  authState: AUTHSTATUS.UNKNOWN,
  accounts: [] as Account[],
  active_account: null,
}

export const reducer = (state = initialState, action: any = {}) => {
  const { type, payload, error } = action
  switch (type) {
    case LOG_IN:
    case REQUEST_USER:
      return { ...state, authState: AUTHSTATUS.PENDING }
    case REQUEST_USER_SUCCESS:
      return {
        ...state,
        authState: AUTHSTATUS.LOGGED_IN,
        error: null,
        ...payload,
      }
    case LOG_IN_SUCCESS:
      return { ...state, authState: AUTHSTATUS.LOGGED_IN, key: payload.key }
    case LOG_IN_FAILED:
      return { ...state, error, authState: AUTHSTATUS.ERROR }
    case REQUEST_USER_FAILED:
      return { ...state, authState: AUTHSTATUS.LOGGED_OUT }
    case LOG_OUT:
      return { ...initialState, authState: AUTHSTATUS.LOGGED_OUT }
    case SET_LANGUAGE:
      return { ...state, language: payload }
    case RESET_PASSWORD_CONFIRM_FAILED:
      return { ...state, error }
    case SWITCH_ACCOUNT:
      return {
        ...state,
        accounts: state.accounts.map(account => ({
          ...account,
          current: account?.id === payload,
        })),
      }
    default:
      return state
  }
}
